import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useOpenToolbarFile } from '#/entities/shipment'
import { TEMPLATE_TYPE, useTemplateOptions } from '#/entities/template'
import {
  insertFieldsToSlashGroups,
  useTemplateVariableOptions,
} from '#/entities/template-variable'
import { EmailEditor } from '#/shared/ui/email-editor'

import type { PanelState } from './panel-state'
import { EmailRecipientsSummary } from './recipients-summary'

interface EmailTabProps {
  email: PanelState['email']
  shipmentId: string | undefined
  compact?: boolean
}

function toEmailAttachments(email: PanelState['email']) {
  return email.orderFiles.map((file) => ({
    id: file.id,
    name: file.name,
    size: file.sizeBytes ?? 0,
    selected: email.selectedFileIds.has(file.id),
  }))
}

export function EmailTab({ email, shipmentId, compact }: EmailTabProps) {
  const { t } = useTranslation()
  const { options: templateOptions, isLoading: templatesLoading } =
    useTemplateOptions({ templateType: TEMPLATE_TYPE.email })
  const { groups: insertFieldGroups, insertValues } =
    useTemplateVariableOptions(shipmentId)
  const slashGroups = useMemo(
    () => insertFieldsToSlashGroups(insertFieldGroups, insertValues),
    [insertFieldGroups, insertValues],
  )
  const openFile = useOpenToolbarFile()
  const fromOptions = email.fromEmail ? [email.fromEmail] : []
  const recipients = email.toEmails.map((value) => ({
    id: value,
    label: value,
  }))
  const attachments = toEmailAttachments(email)
  return (
    <EmailEditor
      compact={compact}
      enableVoice
      from={email.fromEmail}
      fromOptions={fromOptions}
      recipients={recipients}
      onAddRecipient={email.addEmail}
      onRemoveRecipient={email.removeEmail}
      recipientsSummary={
        <EmailRecipientsSummary contacts={email.toEmails} compact={compact} />
      }
      onCcChange={email.onCcChange}
      onBccChange={email.onBccChange}
      subject={email.subject}
      onSubjectChange={email.onSubjectChange}
      templateOptions={templateOptions}
      templateLoading={templatesLoading}
      onTemplateSelect={(id) => void email.applyTemplate(id, insertValues)}
      slashGroups={slashGroups}
      enableAttachments={attachments.length > 0 || email.orderFilesLoading}
      attachments={attachments}
      attachmentsLabel={t('editor.email.orderFiles')}
      attachmentsLoading={email.orderFilesLoading}
      attachmentsSelectable
      attachmentsScrollable
      onViewAttachment={(attachment) => openFile(attachment.id)}
      onToggleAttachment={email.onToggleFile}
      onUpload={email.onUploadAttachment}
      value={email.value}
      onChange={email.onChange}
      onCancel={email.onCancel}
      onSave={(html) => email.onSend(html, insertValues)}
      saveLabel={email.isSending ? t('editor.email.sending') : undefined}
    />
  )
}
